import { modifiableFields, type ModifiableField } from "building-types/options/intersectionBaseTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { DocOption, DocOptionSet, IntersectingOptionSet } from "building-types/options/optionSet";
import type { RWTable } from "common/data/table";
import { getOptionChoicesWithDefaults, isIntersectingOptionSet } from "../optionChoicesTools";
import type { ActiveIntersectionEffect } from "./intersectingOptionSetShared";
import {
  compileIntersectingOptionSet,
  computeIntersectingMatchCounts,
  findIntersectingMatchFromCounts,
  getOptionModifications,
  isModifyIntersection,
  matchIntersectingOption,
  type CompiledIntersectingOptionSet,
} from "./intersectingOptionSetTools";

// Swap the target set's choice contribution in precomputed match counts instead of rematching all combo rows.
function adjustMatchCountsForTargetChoice(
  compiled: CompiledIntersectingOptionSet,
  baseMatchCounts: readonly number[],
  targetOptionSetId: string,
  previousOptionId: string | null | undefined,
  nextOptionId: string,
): number[] {
  const matchCounts = [...baseMatchCounts];

  if (previousOptionId) {
    const previousCandidates = compiled.choiceIndex[targetOptionSetId]?.[previousOptionId];
    if (previousCandidates) {
      for (const candidateIndex of previousCandidates) {
        matchCounts[candidateIndex] = (matchCounts[candidateIndex] ?? 0) - 1;
      }
    }
  }

  const nextCandidates = compiled.choiceIndex[targetOptionSetId]?.[nextOptionId];
  if (nextCandidates) {
    for (const candidateIndex of nextCandidates) {
      matchCounts[candidateIndex] = (matchCounts[candidateIndex] ?? 0) + 1;
    }
  }

  return matchCounts;
}

// Per-row combos list the MODIFY target in their conditions; match-once combos only reference other sets.
function intersectingSetReferencesTargetInConditions(
  optionSet: IntersectingOptionSet,
  targetOptionSetId: string,
): boolean {
  for (const conditions of Object.values(optionSet.conditions)) {
    if (targetOptionSetId in conditions.choices) {
      return true;
    }
  }
  return false;
}

function applyDefinedModifications(baseOption: DocOption, patch: Partial<DocOption>): DocOption | null {
  let modifiedOption: DocOption | null = null;

  for (const field of modifiableFields) {
    const value = patch[field];
    if (value === undefined || value === baseOption[field]) {
      continue;
    }
    if (!modifiedOption) {
      modifiedOption = { ...baseOption };
    }
    (modifiedOption as Record<ModifiableField, unknown>)[field] = value;
  }

  return modifiedOption;
}

interface PerRowIntersectingSetState {
  readonly optionSet: IntersectingOptionSet;
  readonly compiled: CompiledIntersectingOptionSet;
  readonly baseMatchCounts: number[];
}

// Precomputed MODIFY state for optionSetStateSetFromBuilding — built once per building snapshot.
// matchOnceEffectsByTarget = match-once effects; perRowIntersectingSetsByTarget = per-row intersecting sets.
export interface ModifyTargetRowPreview {
  readonly modifyTargetOptionSetIds: ReadonlySet<string>;
  readonly matchOnceEffectsByTarget: ReadonlyMap<string, readonly ActiveIntersectionEffect[]>;
  readonly perRowIntersectingSetsByTarget: ReadonlyMap<string, readonly PerRowIntersectingSetState[]>;
  readonly baseChoices: OptionChoices;
}

// Scans MODIFY intersecting sets once and splits them into match-once vs per-row buckets for each target.
export function buildModifyTargetRowPreview(
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
): ModifyTargetRowPreview | null {
  const baseChoices = getOptionChoicesWithDefaults(definitions, optionChoices);
  const modifyTargetOptionSetIds = new Set<string>();
  const matchOnceEffectsByTarget = new Map<string, ActiveIntersectionEffect[]>();
  const perRowIntersectingSetsByTarget = new Map<string, PerRowIntersectingSetState[]>();

  for (const optionSet of Object.values(definitions.optionSets)) {
    if (!isIntersectingOptionSet(optionSet) || !isModifyIntersection(optionSet)) {
      continue;
    }

    const targetOptionSetId = optionSet.effect.modifySettings?.optionSetId;
    if (!targetOptionSetId) {
      continue;
    }

    modifyTargetOptionSetIds.add(targetOptionSetId);

    if (intersectingSetReferencesTargetInConditions(optionSet, targetOptionSetId)) {
      // Per-row: which combo matches depends on which target option is being previewed.
      const compiled = compileIntersectingOptionSet(optionSet);
      const perRowState: PerRowIntersectingSetState = {
        optionSet,
        compiled,
        baseMatchCounts: computeIntersectingMatchCounts(compiled, baseChoices),
      };
      const existingPerRow = perRowIntersectingSetsByTarget.get(targetOptionSetId);
      if (existingPerRow) {
        existingPerRow.push(perRowState);
      } else {
        perRowIntersectingSetsByTarget.set(targetOptionSetId, [perRowState]);
      }
      continue;
    }

    // Match-once: same combo row matches regardless of which target option we preview.
    const match = matchIntersectingOption(optionSet, baseChoices);
    if (!match) {
      continue;
    }

    const effect: ActiveIntersectionEffect = { ...match, effect: optionSet.effect };
    const existingMatchOnce = matchOnceEffectsByTarget.get(targetOptionSetId);
    if (existingMatchOnce) {
      existingMatchOnce.push(effect);
    } else {
      matchOnceEffectsByTarget.set(targetOptionSetId, [effect]);
    }
  }

  if (modifyTargetOptionSetIds.size === 0) {
    return null;
  }

  return {
    modifyTargetOptionSetIds,
    matchOnceEffectsByTarget,
    perRowIntersectingSetsByTarget,
    baseChoices,
  };
}

// For each enabled row in a MODIFY-targeted set, preview intersection overrides as if that row were selected.
export function previewModifyTargetRows(
  preview: ModifyTargetRowPreview,
  targetOptionSetId: string,
  optionSet: DocOptionSet,
): RWTable<DocOption> | null {
  const matchOnceEffects = preview.matchOnceEffectsByTarget.get(targetOptionSetId) ?? [];
  const perRowStates = preview.perRowIntersectingSetsByTarget.get(targetOptionSetId) ?? [];

  if (matchOnceEffects.length === 0 && perRowStates.length === 0) {
    return null;
  }

  let clonedOptions: RWTable<DocOption> | null = null;
  const currentTargetChoice = preview.baseChoices[targetOptionSetId];

  for (const optionId of Object.keys(optionSet.options)) {
    const baseOption = optionSet.options[optionId];
    if (baseOption.disabled) {
      continue;
    }

    const perRowEffects: ActiveIntersectionEffect[] = [];
    for (const perRowState of perRowStates) {
      const matchCounts = adjustMatchCountsForTargetChoice(
        perRowState.compiled,
        perRowState.baseMatchCounts,
        targetOptionSetId,
        currentTargetChoice,
        optionId,
      );
      const match = findIntersectingMatchFromCounts(perRowState.compiled, matchCounts);
      if (match) {
        perRowEffects.push({ ...match, effect: perRowState.optionSet.effect });
      }
    }

    // Clashing non-price overrides: first match wins; merge order may differ from doc order (BDG-2485).
    const modifications = [...matchOnceEffects, ...perRowEffects];
    if (modifications.length === 0) {
      continue;
    }

    const hypotheticalChoices = { ...preview.baseChoices, [targetOptionSetId]: optionId };
    const modifiedOption = applyDefinedModifications(
      baseOption,
      getOptionModifications(baseOption, modifications, hypotheticalChoices),
    );
    if (!modifiedOption) {
      continue;
    }

    if (!clonedOptions) {
      clonedOptions = { ...optionSet.options };
    }
    clonedOptions[optionId] = modifiedOption;
  }

  return clonedOptions;
}
